import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, X, ThumbsUp, MessageSquare, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_LABEL = {
  direction_updated: 'Direction updated',
  direction_considering: 'Under review',
  cdp_reviewed: 'CDP reviewed',
  parent_linked: 'Access granted',
};

/**
 * Parent inbox, every CDP change notification sent to this parent.
 * NC Gen. Stat. § 115C-158.10 requires parents be notified when the CDP
 * changes. This component also lets the parent Confirm (thumbs-up, tracked in
 * `cdp_notification_ack`) or Decline (raises a counselor review request +
 * routes to the meeting page).
 */
export default function ParentNotificationInbox({ studentId }) {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [collapsed, setCollapsed] = useState(false);
  const [busy, setBusy] = useState({});

  const load = useCallback(() => {
    axios.get(`${API}/nc/cdp/notifications`, getAuthHeader())
      .then((r) => {
        const list = studentId
          ? (r.data.items || []).filter((n) => n.student_id === studentId)
          : (r.data.items || []);
        setItems(list);
        setUnread(list.filter((n) => !n.read_at).length);
      })
      .catch(() => setItems([]));
  }, [getAuthHeader, studentId]);

  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => {
    try {
      await axios.post(`${API}/nc/cdp/notifications/${id}/read`, {}, getAuthHeader());
      load();
    } catch { /* silent */ }
  };

  const acknowledge = async (n, choice) => {
    setBusy((s) => ({ ...s, [n.id]: true }));
    try {
      await axios.post(`${API}/nc/cdp/notifications/${n.id}/acknowledge`,
        { choice }, getAuthHeader());
      if (choice === 'confirm') {
        toast.success("Thanks, logged that you've seen this change.");
      } else {
        toast.success("Counselor notified. Opening the scheduling page.");
      }
      load();
      if (choice === 'decline') {
        setTimeout(() => navigate('/app/counselor-meetings'), 700);
      }
    } catch {
      toast.error('Could not save your response. Try again.');
    } finally {
      setBusy((s) => ({ ...s, [n.id]: false }));
    }
  };

  if (items.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      data-testid="parent-notification-inbox"
      className="relative overflow-hidden rounded-2xl border border-violet-400/25 bg-[#0a1729]/60 p-4 mb-5"
      style={{ boxShadow: '0 20px 60px -50px rgba(167,139,250,0.5)' }}
    >
      <div className="flex items-center gap-2 mb-3">
        <Bell className="h-4 w-4 text-violet-300" />
        <h3 className="text-sm font-semibold text-white">Notifications</h3>
        {unread > 0 && (
          <span className="rounded-full border border-violet-300/40 bg-violet-400/15 px-2 py-0.5 text-[10px] uppercase tracking-wider text-violet-100">
            {unread} unread
          </span>
        )}
        <button
          onClick={() => setCollapsed((c) => !c)}
          className="ml-auto text-[11px] text-slate-300 hover:text-white transition-colors"
        >
          {collapsed ? 'Show' : 'Hide'}
        </button>
      </div>

      <AnimatePresence>
        {!collapsed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="space-y-2 overflow-hidden"
          >
            {items.slice(0, 6).map((n) => {
              const isChange = n.kind !== 'parent_linked';
              const acked = n.acknowledgement;
              return (
                <div key={n.id}
                  data-testid={`inbox-item-${n.id}`}
                  className={`rounded-xl border p-3 ${
                    n.read_at
                      ? 'border-white/8 bg-white/[0.02]'
                      : 'border-violet-400/30 bg-violet-500/[0.06]'
                  }`}
                  style={n.read_at ? {} : { boxShadow: '0 0 18px -8px rgba(167,139,250,0.55)' }}
                >
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-full border border-violet-300/40 bg-violet-400/10 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-violet-100">
                          {KIND_LABEL[n.kind] || n.kind}
                        </span>
                        <span className="text-[10px] text-slate-300">
                          {n.student_name || 'Student'} · {n.created_at ? new Date(n.created_at).toLocaleString() : ''}
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-slate-200 leading-relaxed">{n.message}</p>
                    </div>
                    {!n.read_at && !isChange && (
                      <button
                        onClick={() => markRead(n.id)}
                        data-testid={`inbox-mark-read-${n.id}`}
                        className="p-1 rounded hover:bg-white/10 transition-colors"
                        title="Mark as read"
                      >
                        <X className="h-3 w-3 text-slate-300" />
                      </button>
                    )}
                  </div>

                  {/* Confirm / Decline, only for actual CDP change notifications */}
                  {isChange && (
                    <div className="mt-2.5 flex flex-wrap items-center gap-2">
                      {acked === 'confirm' && (
                        <span className="inline-flex items-center gap-1 text-[11px] text-cyan-300">
                          <Check className="h-3 w-3" /> You confirmed
                        </span>
                      )}
                      {acked === 'decline' && (
                        <span className="inline-flex items-center gap-1 text-[11px] text-pink-300">
                          <MessageSquare className="h-3 w-3" /> Counselor meeting requested
                        </span>
                      )}
                      {!acked && (
                        <>
                          <button
                            onClick={() => acknowledge(n, 'confirm')}
                            disabled={busy[n.id]}
                            data-testid={`inbox-confirm-${n.id}`}
                            className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-[11px] font-semibold text-white bg-gradient-to-r from-cyan-500 to-violet-500 hover:opacity-95 transition-opacity"
                            style={{ boxShadow: '0 0 18px -8px rgba(34,211,238,0.6)' }}>
                            <ThumbsUp className="h-3 w-3" /> Confirm I've seen it
                          </button>
                          <button
                            onClick={() => acknowledge(n, 'decline')}
                            disabled={busy[n.id]}
                            data-testid={`inbox-decline-${n.id}`}
                            className="inline-flex items-center gap-1 rounded-full border border-pink-400/40 bg-pink-500/[0.08] px-3 py-1 text-[11px] text-pink-100 hover:bg-pink-500/[0.15] transition-colors">
                            <MessageSquare className="h-3 w-3" /> Discuss with counselor
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
